import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { FileText, Image as ImageIcon, X, Download, Truck, User, Calendar, Weight } from 'lucide-react';
import { toast } from 'sonner';
import { fmtKg, fmtTs, statusBadgeClass, downloadCsv } from './_utils';
import LiveRoutePlanner from './LiveRoutePlanner';
import ReadOnlyBanner from './ReadOnlyBanner';

const PLACEHOLDERS = {
  picker: 'e.g. 3x SKU-4821 short — substituted with SKU-4830',
  driver: 'e.g. Customer rejected pallet PLT-0042, returned to depot',
  general: 'e.g. Gate code 4491, hi-vis required on site',
};

const TYPE_LABEL = {
  picker: 'Picker', driver: 'Driver/Returns', general: 'General',
};

const TYPE_COLOR = {
  picker: 'bg-sky-100 text-sky-700 border-sky-200',
  driver: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  general: 'bg-amber-100 text-amber-700 border-amber-200',
};

function NotesDrawer({ open, onOpenChange, delivery }) {
  const [notes, setNotes] = useState([]);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ note_type: 'picker', text: '', photos: [] });
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (open && delivery) {
      api.get(`/routeops/notes/${delivery.id}`).then(r => setNotes(r.data || []));
    }
  }, [open, delivery]);

  const handlePhotos = (ev) => {
    const files = Array.from(ev.target.files || []);
    files.forEach(f => {
      const r = new FileReader();
      r.onload = () => setForm(prev => ({ ...prev, photos: [...prev.photos, r.result] }));
      r.readAsDataURL(f);
    });
    ev.target.value = '';
  };

  const removePhoto = (i) => {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, idx) => idx !== i) }));
  };

  const handleSave = async () => {
    if (!form.text.trim()) return toast.error('Note text required');
    try {
      setSaving(true);
      const res = await api.post(`/routeops/notes/${delivery.id}`, form);
      setNotes(n => [res.data, ...n]);
      setForm({ note_type: 'picker', text: '', photos: [] });
      toast.success('Note saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filtered = notes.filter(n => filter === 'all' || n.note_type === filter);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="notes-drawer">
        <SheetHeader className="p-4 border-b border-slate-200 bg-slate-50">
          <SheetTitle className="text-base">{delivery?.business_name}</SheetTitle>
          <div className="text-xs text-slate-500">
            {delivery?.route?.name || 'Unassigned'} · {delivery?.route?.driver_name || 'No driver'}
          </div>
        </SheetHeader>
        <div className="flex gap-1 p-2 border-b border-slate-200 bg-white">
          {['all', 'picker', 'driver', 'general'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-2 py-1 rounded ${filter === f ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`notes-filter-${f}`}
            >
              {f === 'all' ? 'All' : TYPE_LABEL[f]}
            </button>
          ))}
        </div>

        {/* Notes log */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
          {filtered.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-6">No notes yet</div>
          )}
          {filtered.map(n => (
            <div key={n.id} className="bg-white rounded-lg border border-slate-200 p-3 text-sm" data-testid={`note-${n.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase font-mono border rounded px-1.5 py-0.5 ${TYPE_COLOR[n.note_type]}`}>
                  {TYPE_LABEL[n.note_type]}
                </span>
                <span className="text-xs font-medium text-slate-700">{n.author}</span>
                <span className="text-xs text-slate-400 ml-auto">{fmtTs(n.created_at)}</span>
              </div>
              <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
              {n.photos?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {n.photos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setLightbox(p)}
                      className="h-14 w-14 rounded overflow-hidden border border-slate-200 hover:border-blue-400"
                    >
                      <img src={p} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add note */}
        <div className="p-3 border-t border-slate-200 bg-white space-y-2">
          <div className="text-[11px] uppercase font-bold tracking-wide text-slate-500">Add Note</div>
          <div className="flex gap-1">
            {['picker', 'driver', 'general'].map(t => (
              <button
                key={t}
                onClick={() => setForm({ ...form, note_type: t })}
                className={`text-xs px-2 py-1 rounded border ${form.note_type === t ? TYPE_COLOR[t] + ' font-bold' : 'bg-white border-slate-200 text-slate-600'}`}
                data-testid={`note-type-${t}`}
              >
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
          <Textarea
            value={form.text}
            onChange={e => setForm({ ...form, text: e.target.value })}
            placeholder={PLACEHOLDERS[form.note_type]}
            className="min-h-[80px] text-sm"
            data-testid="note-textarea"
          />
          {form.photos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {form.photos.map((p, i) => (
                <div key={i} className="relative h-12 w-12 rounded overflow-hidden border border-slate-200">
                  <img src={p} alt="" className="h-full w-full object-cover" />
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs px-2 py-1.5 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer">
              <ImageIcon className="h-3.5 w-3.5" /> Attach Photo
              <input type="file" accept="image/*" multiple onChange={handlePhotos} className="hidden" data-testid="note-photo-input" />
            </label>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="ml-auto bg-amber-500 hover:bg-amber-600"
              data-testid="note-save"
            >
              {saving ? 'Saving…' : 'Save Note'}
            </Button>
          </div>
        </div>

        {/* Lightbox */}
        {lightbox && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <img src={lightbox} alt="" className="max-w-full max-h-full rounded" />
            <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DeliveryCard({ d, onNotes, onPicking, onDeliver, compact, canEdit = true }) {
  const t = d.terminal || {};
  const picking = !t.picking_complete;
  const delivering = t.picking_complete && !t.delivered;
  const statusLabel = t.delivered ? 'Delivered' : t.out_for_delivery ? 'Out for Delivery' : 'Picking';
  const statusTone = t.delivered
    ? 'bg-emerald-100 text-emerald-700'
    : t.out_for_delivery
    ? 'bg-sky-100 text-sky-700'
    : 'bg-amber-100 text-amber-700 animate-pulse';

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`terminal-card-${d.id}`}>
      <CardContent className={compact ? 'p-3' : 'p-4'}>
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <div className={`font-bold truncate ${compact ? 'text-sm' : 'text-base'}`} style={{ color: 'var(--text-primary)' }}>{d.business_name}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {!compact && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{d.route?.name || '—'}</span>}
              {!compact && <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.route?.driver_name || '—'}</span>}
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{d.date_required}</span>
              <span className="flex items-center gap-1"><Weight className="h-3 w-3" />{d.weight_kg ? fmtKg(d.weight_kg) : 'TBD'}</span>
              {compact && d.picker_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{d.picker_name}</span>}
            </div>
          </div>
          <div className="flex items-start gap-1.5 shrink-0 ml-2">
            <button onClick={() => onNotes(d)} className="p-1 rounded hover:opacity-70" data-testid={`terminal-notes-${d.id}`}>
              <FileText className="h-3.5 w-3.5" style={{ color: 'var(--text-muted)' }} />
            </button>
            <span className={`text-[9px] uppercase font-mono rounded px-1.5 py-0.5 ${statusTone}`}>
              {statusLabel}
            </span>
          </div>
        </div>

        {/* Progress track */}
        <div className={`space-y-1.5 ${compact ? 'mt-2' : 'mt-3'}`}>
          <div className="flex items-center gap-2">
            <div className={`${compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'} rounded-full flex items-center justify-center font-bold ${
              t.picking_complete ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
            }`}>
              {t.picking_complete ? '✓' : '1'}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold ${t.picking_complete ? 'text-emerald-700' : 'text-amber-700'}`}>
                {t.picking_complete ? 'Pick Complete' : 'Picking'}
              </div>
              {t.picking_completed_at && !compact && (
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Completed {fmtTs(t.picking_completed_at)}</div>
              )}
            </div>
            <button
              onClick={() => onPicking(d)}
              disabled={t.picking_complete || !canEdit}
              className={`w-9 h-[18px] rounded-full transition-colors ${
                t.picking_complete ? 'bg-emerald-500' : 'bg-slate-300 hover:bg-amber-400'
              } relative ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`terminal-toggle-picking-${d.id}`}
            >
              <span className={`absolute top-[2px] ${t.picking_complete ? 'right-[2px]' : 'left-[2px]'} w-[14px] h-[14px] bg-white rounded-full shadow transition-all`} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className={`${compact ? 'w-5 h-5 text-[10px]' : 'w-6 h-6 text-xs'} rounded-full flex items-center justify-center font-bold ${
              t.delivered ? 'bg-emerald-500 text-white' :
              t.out_for_delivery ? 'bg-sky-500 text-white' :
              'bg-slate-300 text-slate-500'
            }`}>
              {t.delivered ? '✓' : '2'}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold ${
                t.delivered ? 'text-emerald-700' : t.out_for_delivery ? 'text-sky-700' : 'text-slate-400'
              }`}>
                {t.delivered ? 'Delivered' : t.out_for_delivery ? 'Out for Delivery' : 'Awaiting'}
              </div>
              {t.out_for_delivery_at && !compact && <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Departed {fmtTs(t.out_for_delivery_at)}</div>}
            </div>
            <button
              onClick={() => onDeliver(d)}
              disabled={!t.picking_complete || t.delivered || !canEdit}
              className={`w-9 h-[18px] rounded-full transition-colors relative ${
                t.delivered ? 'bg-emerald-500' :
                t.out_for_delivery ? 'bg-sky-500' :
                t.picking_complete ? 'bg-slate-300 hover:bg-sky-400' : 'bg-slate-200 cursor-not-allowed'
              } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`terminal-toggle-deliver-${d.id}`}
            >
              <span className={`absolute top-[2px] ${(t.delivered || t.out_for_delivery) ? 'right-[2px]' : 'left-[2px]'} w-[14px] h-[14px] bg-white rounded-full shadow transition-all`} />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const ROUTE_ORDER = ['ne', 'se', 'sw', 'nw', 'hgv1', 'hgv2'];
const ROUTE_COLORS = {
  ne: { accent: '#f5a623', bg: 'rgba(245,166,35,0.08)' },
  se: { accent: '#3498db', bg: 'rgba(52,152,219,0.08)' },
  sw: { accent: '#2ecc71', bg: 'rgba(46,204,113,0.08)' },
  nw: { accent: '#9b59b6', bg: 'rgba(155,89,182,0.08)' },
  hgv1: { accent: '#e74c3c', bg: 'rgba(231,76,60,0.08)' },
  hgv2: { accent: '#e67e22', bg: 'rgba(230,126,34,0.08)' },
};

function RouteColumns({ deliveries, onNotes, onPicking, onDeliver, onOpenPlanner, canEdit = true }) {
  // Group deliveries by route_key
  const grouped = {};
  ROUTE_ORDER.forEach(k => { grouped[k] = []; });
  grouped['unassigned'] = [];

  deliveries.forEach(d => {
    const key = d.route_key && ROUTE_ORDER.includes(d.route_key) ? d.route_key : 'unassigned';
    grouped[key].push(d);
  });

  const columns = ROUTE_ORDER.map(key => ({
    key,
    label: deliveries.find(d => d.route_key === key)?.route?.name || key.toUpperCase(),
    driver: deliveries.find(d => d.route_key === key)?.route?.driver_name || '',
    items: grouped[key],
    color: ROUTE_COLORS[key] || { accent: 'var(--text-muted)', bg: 'var(--bg-raised)' },
  }));

  // Only show unassigned column if there are unassigned items
  if (grouped['unassigned'].length > 0) {
    columns.push({
      key: 'unassigned',
      label: 'Unassigned',
      driver: '',
      items: grouped['unassigned'],
      color: { accent: 'var(--text-muted)', bg: 'var(--bg-raised)' },
    });
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 items-start">
      {columns.map(col => (
        <div
          key={col.key}
          className="flex-1 min-w-[220px] max-w-[300px] rounded-lg flex flex-col"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}
          data-testid={`terminal-route-col-${col.key}`}
        >
          {/* Column header */}
          <div className="px-3 py-2.5 rounded-t-lg flex items-center justify-between" style={{ background: col.color.bg, borderBottom: `2px solid ${col.color.accent}` }}>
            <div className="flex items-center gap-2 min-w-0">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: col.color.accent, fontFamily: 'var(--fd)', letterSpacing: '0.06em' }}>
                  {col.label}
                </div>
                {col.driver && (
                  <div className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    <User className="h-3 w-3" />
                    {col.driver}
                  </div>
                )}
              </div>
              {col.key !== 'unassigned' && (
                <button
                  onClick={() => onOpenPlanner && onOpenPlanner(col.key, col.label)}
                  className="shrink-0 hover:scale-110 transition-transform"
                  title="Open Live Route Planner"
                  data-testid={`route-planner-btn-${col.key}`}
                >
                  <img src="/flash-runner.gif" alt="Route Planner" className="h-8 w-8 object-contain" />
                </button>
              )}
            </div>
            <span className="text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5 shrink-0" style={{ background: col.color.accent, color: 'var(--bg-void)', fontFamily: 'var(--fm)', fontSize: '0.65rem' }}>
              {col.items.length}
            </span>
          </div>

          {/* Cards */}
          <div className="p-2 space-y-2">
            {col.items.length === 0 && (
              <div className="text-center py-6 text-[11px]" style={{ color: 'var(--text-muted)' }}>No deliveries</div>
            )}
            {col.items.map(d => (
              <DeliveryCard
                key={d.id} d={d}
                onNotes={() => onNotes(d)}
                onPicking={onPicking}
                onDeliver={onDeliver}
                compact
                canEdit={canEdit}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Table row for the Completed tab showing notes count, inline photo thumbnails and
 * a button to open the full notes drawer. Fetches notes on mount so the table
 * can surface pictures inputted by picker/driver/general at a glance.
 */
function CompletedRow({ delivery, onOpen }) {
  const [notes, setNotes] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    api.get(`/routeops/notes/${delivery.id}`)
      .then(r => { if (!cancelled) setNotes(r.data || []); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [delivery.id]);

  const photos = (notes || []).flatMap(n => (n.photos || []).map(src => ({ src, author: n.author, type: n.note_type })));
  const noteCount = (notes || []).length;
  const preview = (notes || []).find(n => (n.text || '').trim().length > 0);
  const typeCounts = (notes || []).reduce((acc, n) => {
    acc[n.note_type] = (acc[n.note_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <TableRow data-testid={`completed-row-${delivery.id}`}>
      <TableCell className="font-medium">{delivery.business_name}</TableCell>
      <TableCell className="text-xs">{delivery.route?.driver_name || '—'}</TableCell>
      <TableCell className="text-xs">{delivery.route?.name || '—'}</TableCell>
      <TableCell className="font-mono text-xs">{delivery.date_required}</TableCell>
      <TableCell className="text-right font-mono">{delivery.pallets}</TableCell>
      <TableCell className="text-right font-mono">{fmtKg(delivery.weight_kg)}</TableCell>
      <TableCell className="text-xs text-emerald-600">{fmtTs(delivery.terminal?.delivered_at)}</TableCell>
      <TableCell className="py-2">
        <div className="flex flex-col gap-1.5 min-w-[220px]">
          {notes === null ? (
            <span className="text-[10px] text-slate-400">Loading…</span>
          ) : noteCount === 0 ? (
            <button
              onClick={onOpen}
              className="text-[11px] text-slate-400 hover:text-blue-600 self-start"
              data-testid={`completed-open-drawer-${delivery.id}`}
            >
              No notes — add one
            </button>
          ) : (
            <>
              {/* Note type badges */}
              <div className="flex items-center gap-1 flex-wrap">
                {['picker', 'driver', 'general'].map(t => typeCounts[t] ? (
                  <span
                    key={t}
                    className={`text-[9px] uppercase font-mono border rounded px-1 py-0.5 ${TYPE_COLOR[t]}`}
                  >
                    {TYPE_LABEL[t]} · {typeCounts[t]}
                  </span>
                ) : null)}
              </div>
              {/* Latest note preview */}
              {preview && (
                <div className="text-[11px] text-slate-600 line-clamp-2" style={{ maxWidth: '280px' }}>
                  <span className="font-semibold text-slate-700">{preview.author}:</span> {preview.text}
                </div>
              )}
              {/* Photo thumbnails */}
              {photos.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {photos.slice(0, 4).map((p, i) => (
                    <button
                      key={i}
                      onClick={onOpen}
                      className="h-10 w-10 rounded overflow-hidden border border-slate-200 hover:border-blue-400"
                      data-testid={`completed-photo-thumb-${delivery.id}-${i}`}
                      title={`${p.type} · ${p.author}`}
                    >
                      <img src={p.src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                  {photos.length > 4 && (
                    <button
                      onClick={onOpen}
                      className="h-10 w-10 rounded border border-dashed border-slate-300 text-[10px] font-mono text-slate-500 hover:border-blue-400 hover:text-blue-600"
                    >
                      +{photos.length - 4}
                    </button>
                  )}
                </div>
              )}
              <button
                onClick={onOpen}
                className="text-[10px] text-blue-600 hover:underline self-start"
                data-testid={`completed-open-drawer-${delivery.id}`}
              >
                View all {noteCount} note{noteCount === 1 ? '' : 's'} →
              </button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function Terminal() {
  const ctx = useOutletContext() || {};
  const canEdit = ctx.canEditModule ? ctx.canEditModule('routeops_terminal') : true;
  const [tab, setTab] = useState('active');
  const [data, setData] = useState({ active: [], completed: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, delivery: null });
  const [planner, setPlanner] = useState({ open: false, routeKey: null, routeName: '' });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/routeops/terminal');
      setData(res.data);
    } catch {
      toast.error('Failed to load terminal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePicking = async (d) => {
    try {
      await api.post(`/routeops/terminal/${d.id}/picking`);
      fetchData();
    } catch (e) {
      toast.error('Failed');
    }
  };
  const handleDeliver = async (d) => {
    try {
      await api.post(`/routeops/terminal/${d.id}/deliver`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const handleExportCsv = () => {
    downloadCsv(data.completed,
      [
        { label: 'Business', key: 'business_name' },
        { label: 'Driver', get: r => r.route?.driver_name || '' },
        { label: 'Route', get: r => r.route?.name || '' },
        { label: 'Date', key: 'date_required' },
        { label: 'Pallets', key: 'pallets' },
        { label: 'Weight (KG)', key: 'weight_kg' },
        { label: 'Completed', get: r => r.terminal?.delivered_at || '' },
      ],
      'routeops-completed.csv',
    );
    toast.success('CSV exported');
  };

  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [weightUpdate, setWeightUpdate] = useState({ id: null, name: '', weight: '' });

  const handleSearch = async (q) => {
    setSearchQ(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/routeops/terminal/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data || []);
    } catch { setSearchResults([]); }
  };

  const handleWeightSave = async () => {
    if (!weightUpdate.id || !weightUpdate.weight) return;
    try {
      await api.post('/routeops/terminal/update-weight', {
        booking_id: weightUpdate.id,
        weight_kg: Number(weightUpdate.weight),
      });
      toast.success(`Weight updated for ${weightUpdate.name}`);
      setWeightUpdate({ id: null, name: '', weight: '' });
      setSearchResults([]);
      setSearchQ('');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update weight');
    }
  };

  const stats = data.stats || {};

  return (
    <div data-testid="routeops-terminal-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Terminal</h1>
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Live Status</span>
      </div>

      {!canEdit && <ReadOnlyBanner pageName="Terminal" />}

      {/* Stats row — compact */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        {[
          { label: 'Active', value: stats.active || 0, color: 'text-amber-600' },
          { label: 'Out for Delivery', value: stats.out_for_delivery || 0, color: 'text-sky-600' },
          { label: 'Completed Today', value: stats.completed_today || 0, color: 'text-emerald-600' },
          { label: 'Overweight Alerts', value: stats.overweight || 0, color: 'text-red-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-2.5 flex items-center justify-between gap-3">
              <div className="text-[10px] uppercase tracking-wide font-semibold leading-tight" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
              <div className={`text-xl font-bold font-mono ${s.color}`}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weight Update Panel — compact, hidden for view-only users */}
      {canEdit && (
      <Card className="mb-3">
        <CardContent className="p-2.5">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase font-bold tracking-wide whitespace-nowrap" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fd)' }}>Update Weight</div>
            <div className="relative flex-1 max-w-sm">
              <Input
                value={searchQ}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by business name..."
                className="h-8 text-xs"
                data-testid="terminal-weight-search"
              />
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-20 max-h-48 overflow-auto" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-color)' }}>
                  {searchResults.map(r => (
                    <button
                      key={r.id}
                      onClick={() => {
                        setWeightUpdate({ id: r.id, name: r.business_name, weight: r.weight_kg || '' });
                        setSearchResults([]);
                        setSearchQ(r.business_name);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:opacity-80 flex justify-between items-center"
                      style={{ borderBottom: '1px solid var(--border-color)' }}
                    >
                      <span style={{ color: 'var(--text-primary)' }}>{r.business_name}</span>
                      <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                        {r.weight_kg ? `${r.weight_kg} KG` : 'No weight'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {weightUpdate.id && (
              <>
                <Input
                  type="number"
                  min="0"
                  value={weightUpdate.weight}
                  onChange={(e) => setWeightUpdate({ ...weightUpdate, weight: e.target.value })}
                  placeholder="Weight (KG)"
                  className="h-8 w-28 text-xs"
                  data-testid="terminal-weight-input"
                />
                <Button
                  size="sm"
                  onClick={handleWeightSave}
                  className="h-8 text-xs"
                  style={{ background: 'var(--amber)', color: 'var(--bg-void)' }}
                  data-testid="terminal-weight-save"
                >
                  Save
                </Button>
              </>
            )}
          </div>
          {weightUpdate.name && (
            <div className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>
              Updating: <strong>{weightUpdate.name}</strong>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active" data-testid="terminal-tab-active">
            Active <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">{data.active.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="terminal-tab-completed">
            Completed <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200">{data.completed.length}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-4">
        {tab === 'active' && (
          <>
            {loading && <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading...</div>}
            {!loading && data.active.length === 0 && (
              <div className="text-center py-8 rounded-lg" style={{ color: 'var(--text-muted)', background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
                No active deliveries
              </div>
            )}
            {!loading && data.active.length > 0 && (
              <RouteColumns
                deliveries={data.active}
                onNotes={(d) => setDrawer({ open: true, delivery: d })}
                onPicking={handlePicking}
                onDeliver={handleDeliver}
                onOpenPlanner={(key, name) => setPlanner({ open: true, routeKey: key, routeName: name })}
                canEdit={canEdit}
              />
            )}
          </>
        )}
        {tab === 'completed' && (
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="terminal-export-csv">
                  <Download className="h-3.5 w-3.5 mr-1" /> CSV
                </Button>
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Business</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Plt</TableHead>
                      <TableHead className="text-right">Weight</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead className="text-center">Notes &amp; Photos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.completed.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">No completed deliveries</TableCell></TableRow>
                    )}
                    {data.completed.map(d => (
                      <CompletedRow key={d.id} delivery={d} onOpen={() => setDrawer({ open: true, delivery: d })} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <NotesDrawer
        open={drawer.open}
        onOpenChange={(o) => setDrawer({ ...drawer, open: o })}
        delivery={drawer.delivery}
      />

      {planner.open && (
        <LiveRoutePlanner
          routeKey={planner.routeKey}
          routeName={planner.routeName}
          onClose={() => setPlanner({ open: false, routeKey: null, routeName: '' })}
        />
      )}
    </div>
  );
}
